import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, X, Ban, UserCheck } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';

interface AccountActionsProps {
  userId: string;
  username: string | null;
  /** @deprecated Use isBanned instead */
  isDisabled?: boolean;
  isBanned?: boolean;
  /** @deprecated Use onBan instead */
  onDisable?: (userId: string) => Promise<void>;
  /** @deprecated Use onUnban instead */
  onEnable?: (userId: string) => Promise<void>;
  onBan?: (userId: string, reason?: string) => Promise<void>;
  onUnban?: (userId: string) => Promise<void>;
}

/**
 * AccountActions component provides UI for banning/unbanning user accounts
 * with confirmation dialogs and optional ban reason input
 * Updated for Better Auth integration
 * 
 * @param {AccountActionsProps} props - Component properties
 * @returns {JSX.Element} Rendered account actions component
 */
export function AccountActions({
  userId,
  username,
  isDisabled,
  isBanned,
  onDisable,
  onEnable,
  onBan,
  onUnban,
}: AccountActionsProps) {
  const { t } = useTranslation('auth');
  const { user } = useAuth();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [banReason, setBanReason] = useState('');

  // Use isBanned if provided, otherwise fall back to isDisabled
  const accountIsBanned = isBanned ?? isDisabled ?? false;
  
  // Use new handlers with fallback to legacy
  const handleBan = onBan || onDisable;
  const handleUnban = onUnban || onEnable;

  // Check if trying to modify own account
  const isOwnAccount = user?.id === userId;

  const handleAction = async () => {
    if (isOwnAccount) {
      setActionError(t('userAdmin.actions.cannotBanSelf', t('userAdmin.actions.cannotDisableSelf')));
      return;
    }

    if (!handleBan || !handleUnban) {
      setActionError('Action handlers not provided');
      return;
    }

    setLoading(true);
    setActionError(null);

    try {
      if (accountIsBanned) {
        await handleUnban(userId);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        // Pass reason if available and handler supports it
        if (onBan && banReason.trim()) {
          await onBan(userId, banReason.trim());
        } else if (handleBan) {
          await handleBan(userId);
        }
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
      setShowConfirmDialog(false);
      setBanReason('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('userAdmin.actions.error');
      setActionError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setActionError(null);
    setBanReason('');
  };

  return (
    <div className="mt-6 pt-6 border-t border-neutral-200">
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">
        {t('userAdmin.actions.title', 'Account Actions')}
      </h3>

      {/* Success Message */}
      {showSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <Check className="text-green-600 flex-shrink-0" size={18} />
          <p className="text-green-800 text-sm font-medium">
            {accountIsBanned
              ? t('userAdmin.actions.banSuccess', t('userAdmin.actions.disableSuccess'))
              : t('userAdmin.actions.unbanSuccess', t('userAdmin.actions.enableSuccess'))}
          </p>
        </div>
      )}

      {/* Error Message */}
      {actionError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <p className="text-red-800 text-sm font-medium">{actionError}</p>
          </div>
          <button
            onClick={() => setActionError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Action Button */}
      {!showConfirmDialog ? (
        <button
          onClick={() => setShowConfirmDialog(true)}
          disabled={isOwnAccount}
          className={`
            w-full px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2
            ${accountIsBanned
              ? 'bg-green-600 hover:bg-green-700 text-white'
              : 'bg-red-600 hover:bg-red-700 text-white'
            }
            ${isOwnAccount ? 'opacity-50 cursor-not-allowed' : 'shadow-sm hover:shadow-md'}
          `}
        >
          {accountIsBanned ? (
            <>
              <UserCheck size={18} />
              {t('userAdmin.actions.unbanAccount', t('userAdmin.actions.enableAccount'))}
            </>
          ) : (
            <>
              <Ban size={18} />
              {t('userAdmin.actions.banAccount', t('userAdmin.actions.disableAccount'))}
            </>
          )}
        </button>
      ) : (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="font-medium text-yellow-900 mb-1">
                {accountIsBanned 
                  ? t('userAdmin.actions.confirmUnbanTitle', 'Unban Account?')
                  : t('userAdmin.actions.confirmBanTitle', 'Ban Account?')}
              </p>
              <p className="text-sm text-yellow-800">
                {accountIsBanned
                  ? t('userAdmin.actions.confirmUnban', t('userAdmin.actions.confirmEnable'))
                  : t('userAdmin.actions.confirmBan', t('userAdmin.actions.confirmDisable'))}
              </p>
              {username && (
                <p className="text-sm text-yellow-800 mt-2 font-medium">
                  User: {username}
                </p>
              )}
            </div>
          </div>

          {/* Ban Reason Input (only when banning) */}
          {!accountIsBanned && (
            <div className="mb-4">
              <label htmlFor="banReason" className="block text-sm font-medium text-yellow-900 mb-1">
                {t('userAdmin.actions.banReasonLabel', 'Reason (optional)')}
              </label>
              <textarea
                id="banReason"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder={t('userAdmin.actions.banReasonPlaceholder', 'Enter reason for banning this user...')}
                className="w-full px-3 py-2 border border-yellow-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                rows={2}
                maxLength={500}
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleAction}
              disabled={loading}
              className={`
                flex-1 px-4 py-2 rounded-lg font-medium transition-all
                ${accountIsBanned
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
                }
                ${loading ? 'opacity-50 cursor-wait' : ''}
              `}
            >
              {loading ? t('userAdmin.actions.processing', 'Processing...') : t('userAdmin.actions.confirm')}
            </button>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-lg font-medium bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-all"
            >
              {t('userAdmin.actions.cancel')}
            </button>
          </div>
        </div>
      )}

      {isOwnAccount && (
        <p className="mt-2 text-xs text-neutral-500 text-center">
          {t('userAdmin.actions.cannotBanSelf', t('userAdmin.actions.cannotDisableSelf'))}
        </p>
      )}
    </div>
  );
}
